import { Box, createStyles, Flex, Group } from '@mantine/core';
import { trackEvent } from '@repo/common/analytics';
import { useNavigate } from '@repo/common/hooks/useNavigate';
import { Badge, Breadcrumbs, Button } from '@repo/foundations';
import { includes, isNil, noop } from 'lodash-es';
import { useMemo } from 'react';
import { Link } from 'react-router';
import type { ISecodaEntity } from '../../../api';
import { AutomationJobActionBar } from '../../../components/AutomationV2/JobDrawer/AutomationJobActionBar';
import EntityPageBreadcrumbs from '../../../components/EntityPageLayout/EntityPageBreadcrumbs';
import { TaskActions } from '../components/actions/TaskActions';
import { ANALYTICS_EVENTS } from '../constants';
import type { IInboxViewHeaderProps } from '../types';
import { getNotificationType } from '../utils/notifications';
import {
	isTeamAccessRequestEvent,
	TEAM_ACCESS_REQUEST_CREATED_EVENT,
} from '../utils/teamAccessRequestUtils';

const useStyles = createStyles((theme) => ({
	container: {
		width: '100%',
		height: 52,
		padding: `${theme.spacing['sm']} ${theme.spacing['xl']}`,
	},
	dropdown: {
		maxHeight: 500,
		overflowY: 'auto',
	},
	leftGroup: {
		flex: 1,
		minWidth: 0,
	},
}));

export function InboxViewHeader({
	selected,
	inboxEventTypes,
	onKeyDown,
	applyChangeRequest,
	applyAccessRequest,
	incident,
	onUpdateIncident,
	changeRequestApplied,
	accessRequestApplied,
	user,
	workspace,
	isCompleteAutomationReviewLoading,
	completeAutomationReviewMutation,
	approveTeamAccessRequest,
	rejectTeamAccessRequest,
	isApprovingTeamAccessRequest,
	isRejectingTeamAccessRequest,
}: IInboxViewHeaderProps) {
	const { classes } = useStyles();
	const navigate = useNavigate();

	const type = useMemo(() => {
		if (isNil(selected)) {
			return null;
		}
		return getNotificationType(inboxEventTypes, selected.event);
	}, [inboxEventTypes, selected]);

	if (isNil(selected)) {
		return null;
	}

	const {
		isIntegration,
		isMonitorIncident,
		isDataAccessRequest,
		isAnnouncement,
		isAiAgent,
		isAutomationJob,
	} = selected.computedMetadata;

	const handleBreadcrumbClick = (href: string, title: string) => {
		trackEvent(
			ANALYTICS_EVENTS.BREADCRUMB_CLICK,
			{
				notificationId: selected.id,
				notificationEvent: selected.event,
				breadcrumbTitle: title,
				destination: href,
				resourceType: selected.resource_type || 'unknown',
			},
			user,
			workspace
		);
	};

	const handleGoToChat = () => {
		const embeddedPromptId = selected.metadata?.embedded_prompt_id as string;
		if (embeddedPromptId) {
			navigate(`/ai/${embeddedPromptId}`);
		}
	};

	const renderBreadcrumbs = () => {
		// Prefer the parent Question for question/question_reply to render proper breadcrumbs
		const breadcrumbEntity =
			(selected.computedMetadata.isQuestion &&
				selected.computedMetadata?.secodaEntity) ||
			selected.entity ||
			selected.computedMetadata?.secodaEntity;
		if (breadcrumbEntity) {
			return (
				<EntityPageBreadcrumbs entity={breadcrumbEntity as ISecodaEntity} />
			);
		}
		if (isMonitorIncident) {
			const crumbs = [
				{ title: 'Monitor', href: '/monitors' },
				{ title: 'Incident', href: '/monitors' },
				...(selected.metadata?.monitor_id
					? [
							{
								title: selected.resource_title || 'Incident Resource',
								href: `/monitor/${selected.metadata?.monitor_id}`,
							},
						]
					: []),
			];
			return (
				<Breadcrumbs
					crumbs={crumbs.map((crumb) => ({
						...crumb,
						onClick: () => handleBreadcrumbClick(crumb.href, crumb.title),
					}))}
				/>
			);
		}
		if (isDataAccessRequest) {
			const crumbs = [
				{ title: 'Requests', href: '/requests' },
				{
					title: selected.resource_title || 'Requested Resource',
					href: `/requests/${selected.resource_id}`,
				},
			];
			return (
				<Breadcrumbs
					crumbs={crumbs.map((crumb) => ({
						...crumb,
						onClick: () => handleBreadcrumbClick(crumb.href, crumb.title),
					}))}
				/>
			);
		}
		if (isTeamAccessRequestEvent(selected.event)) {
			const teamId = selected.metadata?.team_id as string | undefined;
			const teamName =
				(selected.metadata?.team_name as string | undefined) ??
				selected.resource_title ??
				'Team';

			const crumbs = [
				{ title: 'Teams', href: '/teams' },
				...(teamId
					? [{ title: teamName, href: `/teams/${teamId}/` }]
					: [{ title: teamName, href: undefined }]),
			];

			return (
				<Breadcrumbs
					crumbs={crumbs.map((crumb) => ({
						...crumb,
						onClick: crumb.href
							? () => handleBreadcrumbClick(crumb.href!, crumb.title)
							: undefined,
					}))}
				/>
			);
		}
		if (isIntegration) {
			const crumbs = [
				{ title: 'Integration', href: '/integrations' },
				...(selected.metadata?.integration_id
					? [
							{
								title: selected.resource_title || 'Integration Resource',
								href: `/integrations/${selected.metadata?.integration_id}`,
							},
						]
					: []),
			];
			return (
				<Breadcrumbs
					crumbs={crumbs.map((crumb) => ({
						...crumb,
						onClick: () => handleBreadcrumbClick(crumb.href, crumb.title),
					}))}
				/>
			);
		}

		if (isAnnouncement) {
			return <Breadcrumbs crumbs={[{ title: 'Announcement' }]} />;
		}

		if (isAiAgent) {
			const crumbs = [
				{ title: 'AI Agent', href: '/ai/new' },
				{
					title: selected.resource_title || 'AI Task',
					href: `/ai/${selected.metadata?.embedded_prompt_id}`,
				},
			];
			return (
				<Breadcrumbs
					crumbs={crumbs.map((crumb) => ({
						...crumb,
						onClick: () => handleBreadcrumbClick(crumb.href, crumb.title),
					}))}
				/>
			);
		}

		if (isAutomationJob && selected.computedMetadata.automationJob) {
			const { automationJob } = selected.computedMetadata;
			const crumbs = [
				{ title: 'Automations', href: '/automations' },
				{
					title: selected.resource_title || 'Untitled',
					href: automationJob.automation
						? `/automation/${automationJob.automation}`
						: undefined,
				},
			];
			return (
				<Breadcrumbs
					crumbs={crumbs.map((crumb) => ({
						...crumb,
						onClick: crumb.href
							? () => handleBreadcrumbClick(crumb.href!, crumb.title)
							: undefined,
					}))}
				/>
			);
		}

		return null;
	};

	if (
		selected.computedMetadata.isAutomationJob &&
		selected.computedMetadata.automationJob &&
		completeAutomationReviewMutation &&
		!isNil(isCompleteAutomationReviewLoading)
	) {
		return (
			<Flex
				className={classes.container}
				tabIndex={0}
				onKeyDown={onKeyDown}
				justify="space-between"
			>
				<Group className={classes.leftGroup}>
					<Box style={{ flex: 1, minWidth: 0 }}>{renderBreadcrumbs()}</Box>
				</Group>
				<AutomationJobActionBar
					hideStartedTime={true}
					automationJob={selected.computedMetadata.automationJob}
					onClose={noop}
					handleCompleteAutomationReview={completeAutomationReviewMutation}
					isCompleteAutomationReviewLoading={isCompleteAutomationReviewLoading}
					includeCloseButton={false}
				/>
			</Flex>
		);
	}

	return (
		<Flex
			className={classes.container}
			tabIndex={0}
			onKeyDown={onKeyDown}
			justify={
				selected.event === 'resource_archived' ? 'flex-start' : 'space-between'
			}
		>
			<Group className={classes.leftGroup}>
				<Box style={{ flex: 1, minWidth: 0 }}>{renderBreadcrumbs()}</Box>
				{selected.event === 'resource_archived' && (
					<Badge variant="warning">Deleted</Badge>
				)}
			</Group>
			{(type === 'tasks' ||
				includes(
					[
						'access_request',
						'change_request',
						TEAM_ACCESS_REQUEST_CREATED_EVENT,
						'data_access_request_created',
						'data_access_request_approved',
						'data_access_request_approved_admin',
						'data_access_request_rejected',
						'data_access_request_cancelled',
					],
					selected.event
				)) && (
				<Group spacing="xs">
					<TaskActions
						selected={selected}
						applyChangeRequest={applyChangeRequest}
						applyAccessRequest={applyAccessRequest}
						changeRequestApplied={changeRequestApplied}
						accessRequestApplied={accessRequestApplied}
						incident={incident}
						onUpdateIncident={onUpdateIncident}
						user={user}
						workspace={workspace}
						approveTeamAccessRequest={approveTeamAccessRequest}
						rejectTeamAccessRequest={rejectTeamAccessRequest}
						isApprovingTeamAccessRequest={isApprovingTeamAccessRequest}
						isRejectingTeamAccessRequest={isRejectingTeamAccessRequest}
					/>
				</Group>
			)}
			{isIntegration && selected.metadata?.integration_id && (
				<Group>
					<Button
						component={Link}
						to={`/integrations/${selected.metadata.integration_id}/syncs`}
						size="md"
					>
						View syncs
					</Button>
				</Group>
			)}
			{isAiAgent && selected.metadata?.embedded_prompt_id && (
				<Group>
					<Button size="md" onClick={handleGoToChat}>
						Go to chat
					</Button>
				</Group>
			)}
		</Flex>
	);
}
